const TOOLTIP_COMMIT_COUNT = 3;
const TOOLTIP_TITLE_LENGTH = 70;

export const shortSha = (sha) => (sha ? String(sha).slice(0, 7) : '?');

const repoName = (repo) => String(repo || '').split('/').pop();

function sdkApp(run) {
  return (run?.metadata?.apps || []).find((app) => app.role === 'sdk') || null;
}

// The repo and commit the run's SDK test app was built from. Runs uploaded
// before app sources were recorded fall back to the iOS run commit.
function appSource(run) {
  const app = sdkApp(run);
  if (app?.sourceRepo && app.sourceSha) return { repo: app.sourceRepo, sha: app.sourceSha, branch: app.sourceBranch || null };
  const match = /github\.com\/(ContentSquare\/cs-ios-sdk)\//.exec(run?.url || '');
  if (match && run?.commit) return { repo: match[1], sha: run.commit, branch: run.branch || null };
  return null;
}

const compareUrl = (repo, base, head) => `https://github.com/${repo}/compare/${base}...${head}`;

// Joins the per-run diffs of consecutive runs into one diff, newest first.
function joinDiffs(diffs) {
  if (diffs.length === 1) return diffs[0];
  const { repo, head } = diffs[0];
  const { base } = diffs[diffs.length - 1];
  if (diffs.some((diff) => diff.repo !== repo)) return null;
  const known = diffs.every((diff) => ['ahead', 'identical'].includes(diff.status) && Number.isInteger(diff.totalCommits));
  const bySha = new Map();
  diffs.flatMap((diff) => diff.commits || []).forEach((commit) => {
    if (!bySha.has(commit.sha)) bySha.set(commit.sha, commit);
  });
  const commits = [...bySha.values()];
  const totalCommits = known ? diffs.reduce((sum, diff) => sum + diff.totalCommits, 0) : null;
  let status = 'unknown';
  if (known) status = totalCommits === 0 ? 'identical' : 'ahead';
  return {
    repo,
    base,
    head,
    status,
    sameBuild: base === head,
    totalCommits,
    commits,
    truncated: diffs.some((diff) => diff.truncated),
    htmlUrl: compareUrl(repo, base, head),
  };
}

// Follows each run's diff back to its base run until it reaches the previous
// point, so runs hidden by the page filters still contribute their commits.
function diffSince(run, previousRun, allRuns) {
  const byWorkflowRun = new Map(allRuns
    .filter((other) => other.platform === run.platform && other.workflowRunId)
    .map((other) => [String(other.workflowRunId), other]));
  const diffs = [];
  let current = run;
  while (current && diffs.length <= allRuns.length) {
    const diff = current.metadata?.appDiff;
    if (!diff) return null;
    diffs.push(diff);
    if (String(diff.baseWorkflowRunId) === String(previousRun.workflowRunId)) return joinDiffs(diffs);
    current = byWorkflowRun.get(String(diff.baseWorkflowRunId));
  }
  return null;
}

function previousIndex(values, index) {
  for (let earlier = index - 1; earlier >= 0; earlier -= 1) {
    if (values[earlier] !== null && values[earlier] !== undefined) return earlier;
  }
  return -1;
}

// How the SDK test app changed between a trend point and the previous point
// drawn on the same line.
export function appChangeAt(runs, values, index, allRuns = runs) {
  const run = runs[index];
  const before = previousIndex(values, index);
  if (!run || before < 0) return null;
  const previousRun = runs[before];
  const head = appSource(run);
  const base = appSource(previousRun);
  const diff = diffSince(run, previousRun, allRuns);
  const context = { run, previousRun, branch: head?.branch || null, commits: [] };
  if (diff) {
    const kind = diff.sameBuild || diff.status === 'identical' ? 'same' : 'diff';
    return {
      ...context,
      kind,
      repo: diff.repo,
      base: diff.base,
      head: diff.head,
      status: diff.status,
      totalCommits: diff.totalCommits,
      commits: diff.commits || [],
      truncated: diff.truncated,
      htmlUrl: diff.htmlUrl,
    };
  }
  if (!head || !base || head.repo !== base.repo) return { ...context, kind: 'unknown' };
  if (head.sha === base.sha) {
    return { ...context, kind: 'same', repo: head.repo, base: base.sha, head: head.sha, totalCommits: 0 };
  }
  return {
    ...context,
    kind: 'link',
    repo: head.repo,
    base: base.sha,
    head: head.sha,
    totalCommits: null,
    htmlUrl: compareUrl(head.repo, base.sha, head.sha),
  };
}

const truncate = (text, length) => (text.length > length ? `${text.slice(0, length - 1)}…` : text);

const commitCount = (count) => `${count} commit${count === 1 ? '' : 's'}`;

export function appChangeTooltipLines(change) {
  if (!change) return [];
  if (change.kind === 'unknown') return ['', 'Test app source unknown for this run'];
  const branch = change.branch ? ` (${change.branch})` : '';
  const range = `Test app: ${repoName(change.repo)} ${shortSha(change.base)} → ${shortSha(change.head)}${branch}`;
  if (change.kind === 'same') return ['', range, 'Same test app build as the previous run'];
  if (change.kind === 'link') return ['', range, 'Click the point for the GitHub comparison'];
  if (change.status === 'unknown' || change.totalCommits === null || change.totalCommits === undefined) {
    return ['', range, 'Commit list unavailable, click the point for the GitHub comparison'];
  }
  if (change.status === 'behind') return ['', range, 'Older build than the previous run', 'Click the point for the GitHub comparison'];
  const lines = ['', `${range} · ${commitCount(change.totalCommits)}`];
  change.commits.slice(0, TOOLTIP_COMMIT_COUNT).forEach((commit) => lines.push(`• ${truncate(commit.title || shortSha(commit.sha), TOOLTIP_TITLE_LENGTH)}`));
  const more = change.totalCommits - Math.min(change.commits.length, TOOLTIP_COMMIT_COUNT);
  if (more > 0) lines.push(`…and ${more} more`);
  lines.push('Click the point for the commit list');
  return lines;
}
